import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
    Share2, Twitter, Copy, Check, AlertCircle,
    Repeat2, Megaphone, Mail, Quote, Video, Layers,
    Link, MousePointer, FileText, Image, ChevronDown, ChevronUp,
    Save, CheckCircle2, RefreshCw, Pencil, Loader2, X,
    Send, ExternalLink, Trash2, Type as TextIcon,
} from 'lucide-react';
import {
    GlassCard, GlassButton, GlassTabs, GlassSelect, ModuleHeader, GlassBadge
} from '../ui';
import {
    getBrandGuides, getBrandGuidesByProject, BrandGuide, getProjects, Project,
    buildTwitterThread, generateLinkedInCarousel,
    generateAdCopy,
    buildUtmUrl, checkMessageMatch, listUtmLinks, deleteUtmLink, UtmLinkRow,
    getProjectArticles, repurposeArticle, saveMultichannelOutput,
    getArticleMultichannelContent, regenerateSection, SavedTaskRef,
    selectImagesForTask, updateHeroImage, deleteTask, getSocialAccounts,
    getTask, updateTask,
    TwitterThread, LinkedInCarousel, AdCopyResult,
    ProjectArticle, RepurposeArticleResponse, OverlayState,
} from '../../services/api';
import TextOverlayModal from '../shared/TextOverlayModal';
import ImagePickerModal from '../shared/ImagePickerModal';
import SocialAssetEditor from '../shared/SocialAssetEditor';
import SocialPublishModal from '../SocialPublishModal';
import './MultiChannel.css';
import '../shared/AssetCardGrid.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const TABS = [
    { id: 'repurpose-article', label: 'Repurpose Article' },
    { id: 'social-posts', label: 'Social Posts' },
    { id: 'carousel', label: 'LinkedIn Carousel' },
    { id: 'thread', label: 'Thread Builder' },
    { id: 'video-script', label: 'Video Script' },
    { id: 'email-sequence', label: 'Drip Sequence' },
    { id: 'adcopy', label: 'Ad Copy' },
    { id: 'utm', label: 'UTM Builder' },
    { id: 'message-match', label: 'Message Match' },
];

const AD_PLATFORMS = [
    { id: 'google_search', label: 'Google Search' },
    { id: 'meta_facebook', label: 'Meta / Facebook' },
    { id: 'linkedin_sponsored', label: 'LinkedIn Sponsored' },
];

// ---------------------------------------------------------------------------
// Reusable sub-components
// ---------------------------------------------------------------------------

const LoadingState: React.FC<{ message?: string }> = ({ message = 'Generating with AI...' }) => (
    <div className="mc-loading">
        <div className="mc-spinner" />
        <span>{message}</span>
    </div>
);

const ErrorState: React.FC<{ message: string }> = ({ message }) => (
    <div className="mc-error">
        <AlertCircle size={16} />
        <span>{message}</span>
    </div>
);

const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // fallback — clipboard API blocked
        }
    }, [text]);

    return (
        <button
            className={`mc-copy-btn ${copied ? 'copied' : ''}`}
            onClick={handleCopy}
            title="Copy to clipboard"
        >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied' : (label || 'Copy')}
        </button>
    );
};

interface PlatformCheckboxGroupProps {
    options: { id: string; label: string }[];
    selected: string[];
    onChange: (selected: string[]) => void;
}

const PlatformCheckboxGroup: React.FC<PlatformCheckboxGroupProps> = ({ options, selected, onChange }) => {
    const toggle = (id: string) => {
        if (selected.includes(id)) {
            onChange(selected.filter(s => s !== id));
        } else {
            onChange([...selected, id]);
        }
    };

    return (
        <div className="mc-platform-checkboxes">
            {options.map(opt => (
                <label
                    key={opt.id}
                    className={`mc-platform-checkbox ${selected.includes(opt.id) ? 'checked' : ''}`}
                >
                    <input
                        type="checkbox"
                        checked={selected.includes(opt.id)}
                        onChange={() => toggle(opt.id)}
                    />
                    {opt.label}
                </label>
            ))}
        </div>
    );
};

interface BrandGuideSelectorProps {
    brandGuideId: string;
    brandGuides: BrandGuide[];
    onChange: (id: string) => void;
}

const BrandGuideSelector: React.FC<BrandGuideSelectorProps> = ({ brandGuideId, brandGuides, onChange }) => {
    const options = [
        { value: '', label: 'No brand guide' },
        ...brandGuides.map(bg => ({ value: bg.brand_guide_id, label: bg.brand_guide_name })),
    ];

    return (
        <div className="mc-form-group">
            <label>Brand Guide (optional)</label>
            <GlassSelect
                options={options}
                value={brandGuideId}
                onChange={onChange}
                placeholder="Select brand guide..."
            />
        </div>
    );
};

function getPlatformIconClass(platform: string): string {
    const p = platform.toLowerCase();
    if (p === 'linkedin') return 'linkedin';
    if (p === 'twitter') return 'twitter';
    if (p === 'instagram') return 'instagram';
    if (p === 'facebook') return 'facebook';
    return '';
}

function getPlatformLabel(platform: string): string {
    const p = platform.toLowerCase();
    if (p === 'linkedin') return 'LI';
    if (p === 'twitter') return 'X';
    if (p === 'instagram') return 'IG';
    if (p === 'facebook') return 'FB';
    return platform.slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// Empty state for content tabs that require a repurposed article
// ---------------------------------------------------------------------------

const ContentEmptyState: React.FC<{ label: string; onSwitchTab: (tab: string) => void }> = ({ label, onSwitchTab }) => (
    <GlassCard className="mc-form-card">
        <div className="mc-empty-state">
            <FileText size={32} />
            <p>Select and repurpose an article first to see {label} here.</p>
            <GlassButton variant="secondary" onClick={() => onSwitchTab('repurpose-article')}>
                <Repeat2 size={14} />
                Go to Repurpose Article
            </GlassButton>
        </div>
    </GlassCard>
);

// ---------------------------------------------------------------------------
// Tab: Thread Builder
// ---------------------------------------------------------------------------

/**
 * Parse a Gemini-generated thread string into structured tweets.
 * Splits on blank lines, strips numbering like "1/", "2/ ", "1)", "1." prefixes,
 * trims whitespace, drops empty entries. Char-counts each piece for the badge.
 * Fallback: if the model didn't double-newline, splits on lone "N/" prefixes.
 */
function parseThreadString(raw: string): { number: number; text: string; char_count: number }[] {
    if (!raw || typeof raw !== 'string') return [];
    const blocks = raw.split(/\n{2,}/g).map(s => s.trim()).filter(Boolean);
    // Fallback: if the model didn't double-newline, split on lone "N/" prefixes.
    const pieces = blocks.length > 1
        ? blocks
        : raw.split(/(?:^|\n)\s*(?=\d+\s*[\/\.\)])/g).map(s => s.trim()).filter(Boolean);
    return pieces.map((t, i) => {
        const cleaned = t.replace(/^\s*\d+\s*[\/\.\)]\s*/, '').trim();
        return { number: i + 1, text: cleaned, char_count: cleaned.length };
    }).filter(t => t.text.length > 0);
}

const ThreadBuilderTab: React.FC<{
    brandGuides: BrandGuide[];
    projects?: Project[];
    defaultProjectId?: string;
    prefill?: string;
    brandGuideId: string;
    repurposeResult: RepurposeArticleResponse | null;
    onUpdate: (r: RepurposeArticleResponse) => void;
    selectedArticleId: string;
    projectId: string;
    onSwitchTab: (tab: string) => void;
}> = ({ brandGuides, projects, defaultProjectId, prefill, brandGuideId, repurposeResult, onUpdate, selectedArticleId, projectId, onSwitchTab }) => {
    const [content, setContent] = useState(prefill || '');
    const [topic, setTopic] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<TwitterThread | null>(null);
    const [regenLoading, setRegenLoading] = useState(false);
    const [regenInstructions, setRegenInstructions] = useState('');
    const [showRefine, setShowRefine] = useState(false);

    // Repurpose-generated thread string (stored under twitter_thread key — do not change this key)
    const threadFromRepurpose = repurposeResult?.repurpose_result?.social_posts?.twitter_thread;
    const parsedRepurposeTweets = React.useMemo(
        () => threadFromRepurpose ? parseThreadString(String(threadFromRepurpose)) : [],
        [threadFromRepurpose]
    );

    const handleSubmit = async () => {
        if (!content.trim()) { setError('Content is required.'); return; }
        setError(null);
        setLoading(true);
        try {
            const data = await buildTwitterThread({
                content: content.trim(),
                topic: topic.trim() || undefined,
                brand_guide_id: brandGuideId || undefined,
                project_id: projectId || undefined,
            });
            setResult(data);
        } catch (err: any) {
            setError(getApiError(err, 'Thread generation failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerate = async () => {
        if (!selectedArticleId || !projectId) return;
        setRegenLoading(true);
        try {
            const resp = await regenerateSection({
                task_id: selectedArticleId,
                project_id: projectId,
                section: 'social_twitter',
                instructions: regenInstructions.trim() || undefined,
                brand_guide_id: brandGuideId || undefined,
            });
            if (resp.multichannel) onUpdate(resp.multichannel);
            setRegenInstructions('');
            setShowRefine(false);
        } catch (err: any) {
            setError(getApiError(err, 'Regeneration failed.'));
        } finally {
            setRegenLoading(false);
        }
    };

    const fullThread = result?.tweets.map(t => t.text).join('\n\n') || '';

    return (
        <>
            {/* Show repurposed thread content if available */}
            {threadFromRepurpose && !result && (
                <GlassCard className="mc-results-card">
                    <div className="mc-results-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8, borderBottom: '1px solid var(--stroke)' }}>
                            <h3 style={{ margin: 0, border: 'none', padding: 0 }}>
                                Twitter/X Thread (from article)
                            </h3>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <CopyButton text={String(threadFromRepurpose)} label="Copy All" />
                            </div>
                        </div>
                        {parsedRepurposeTweets.length > 0 ? (
                            <div className="mc-tweet-list">
                                {parsedRepurposeTweets.map((tweet, i) => (
                                    <div key={i} className="mc-tweet-card">
                                        <div className="mc-tweet-header">
                                            <span className="mc-tweet-number">Tweet {tweet.number}</span>
                                            <div className="mc-tweet-meta">
                                                <span className={`mc-tweet-char ${tweet.char_count <= 280 ? 'ok' : 'over'}`}>
                                                    {tweet.char_count} / 280
                                                </span>
                                                <CopyButton text={tweet.text} />
                                            </div>
                                        </div>
                                        <div className="mc-tweet-text">{tweet.text}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="mc-platform-content">{String(threadFromRepurpose)}</div>
                        )}
                    </div>
                    {selectedArticleId && projectId && (
                        <div className="mc-refine-area">
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <GlassButton variant="secondary" onClick={() => setShowRefine(!showRefine)} disabled={regenLoading}>
                                    <Pencil size={14} />
                                    Refine
                                </GlassButton>
                                <GlassButton variant="secondary" className="mc-regen-btn" onClick={handleRegenerate} disabled={regenLoading}>
                                    <RefreshCw size={14} className={regenLoading ? 'mc-spin-icon' : ''} />
                                    {regenLoading ? 'Regenerating...' : 'Regenerate'}
                                </GlassButton>
                            </div>
                            {showRefine && (
                                <div className="mc-form-group" style={{ marginTop: 10 }}>
                                    <textarea
                                        placeholder="Add instructions for regeneration (e.g. make it more casual, focus on the key takeaway)..."
                                        value={regenInstructions}
                                        onChange={e => setRegenInstructions(e.target.value)}
                                        style={{ minHeight: 80 }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </GlassCard>
            )}

            {/* Manual thread builder form */}
            <GlassCard className="mc-form-card">
                <div className="mc-form-group">
                    <label>Source Content *</label>
                    <textarea
                        className="tall"
                        placeholder="Paste the content you want to turn into a Twitter/X thread..."
                        value={content}
                        onChange={e => setContent(e.target.value)}
                    />
                </div>
                <div className="mc-form-group">
                    <label>Topic / Angle (optional)</label>
                    <input
                        type="text"
                        className="mc-text-input"
                        style={{
                            padding: '10px 14px',
                            background: 'var(--panel)',
                            border: '1px solid var(--stroke)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--text)',
                            fontSize: '0.9rem',
                            fontFamily: 'var(--font-family)',
                            outline: 'none',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                        placeholder="e.g. marketing tips, productivity, AI trends"
                        value={topic}
                        onChange={e => setTopic(e.target.value)}
                    />
                </div>
                {error && <ErrorState message={error} />}
                <div className="mc-form-actions">
                    <GlassButton variant="primary" onClick={handleSubmit} disabled={loading}>
                        <Twitter size={16} />
                        {loading ? 'Building Thread...' : 'Build Thread'}
                    </GlassButton>
                </div>
                {loading && <LoadingState message="Crafting your Twitter thread..." />}
            </GlassCard>

            {result && !loading && result.tweets.length > 0 && (
                <GlassCard className="mc-results-card">
                    <div className="mc-results-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8, borderBottom: '1px solid var(--stroke)' }}>
                            <h3 style={{ margin: 0, border: 'none', padding: 0 }}>
                                Thread — {result.total_tweets} tweets
                            </h3>
                            <CopyButton text={fullThread} label="Copy All" />
                        </div>
                        <div className="mc-tweet-list">
                            {result.tweets.map((tweet, i) => (
                                <div key={i} className="mc-tweet-card">
                                    <div className="mc-tweet-header">
                                        <span className="mc-tweet-number">Tweet {tweet.number}</span>
                                        <div className="mc-tweet-meta">
                                            <span className={`mc-tweet-char ${tweet.char_count <= 280 ? 'ok' : 'over'}`}>
                                                {tweet.char_count} / 280
                                            </span>
                                            <CopyButton text={tweet.text} />
                                        </div>
                                    </div>
                                    <div className="mc-tweet-text">{tweet.text}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {projects && projects.length > 0 && (
                        <SaveToProjectRow
                            projects={projects}
                            defaultProjectId={defaultProjectId}
                            contentType="thread"
                            contentData={result}
                        />
                    )}
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab: LinkedIn Carousel
// ---------------------------------------------------------------------------

const LinkedInCarouselTab: React.FC<{
    brandGuides: BrandGuide[];
    projects?: Project[];
    defaultProjectId?: string;
    prefillResult?: LinkedInCarousel;
    prefillTopic?: string;
    brandGuideId: string;
    repurposeResult: RepurposeArticleResponse | null;
    onUpdate: (r: RepurposeArticleResponse) => void;
    selectedArticleId: string;
    projectId: string;
    onSwitchTab: (tab: string) => void;
}> = ({ brandGuides, projects, defaultProjectId, prefillResult, prefillTopic, brandGuideId, repurposeResult, onUpdate, selectedArticleId, projectId, onSwitchTab }) => {
    const [content, setContent] = useState('');
    const [topic, setTopic] = useState(prefillTopic || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<LinkedInCarousel | null>(prefillResult || null);
    const [regenLoading, setRegenLoading] = useState(false);
    const [regenInstructions, setRegenInstructions] = useState('');
    const [showRefine, setShowRefine] = useState(false);

    const carouselFromRepurpose = repurposeResult?.repurpose_result?.carousel_outline;

    const handleSubmit = async () => {
        if (!content.trim()) { setError('Content is required.'); return; }
        if (!topic.trim()) { setError('Topic is required.'); return; }
        setError(null);
        setLoading(true);
        try {
            const data = await generateLinkedInCarousel({
                content: content.trim(),
                topic: topic.trim(),
                brand_guide_id: brandGuideId || undefined,
            });
            setResult(data);
        } catch (err: any) {
            setError(getApiError(err, 'Carousel generation failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerate = async () => {
        if (!selectedArticleId || !projectId) return;
        setRegenLoading(true);
        try {
            const resp = await regenerateSection({
                task_id: selectedArticleId,
                project_id: projectId,
                section: 'carousel',
                instructions: regenInstructions.trim() || undefined,
                brand_guide_id: brandGuideId || undefined,
            });
            if (resp.multichannel) onUpdate(resp.multichannel);
            setRegenInstructions('');
            setShowRefine(false);
        } catch (err: any) {
            setError(getApiError(err, 'Regeneration failed.'));
        } finally {
            setRegenLoading(false);
        }
    };

    // Display carousel (from repurpose or standalone generation)
    const displayCarousel = result || carouselFromRepurpose;

    return (
        <>
            {/* Repurposed carousel with regenerate */}
            {carouselFromRepurpose && !result && (
                <GlassCard className="mc-results-card">
                    <div className="mc-carousel-meta">
                        <div className="mc-carousel-meta-item">
                            <span className="mc-carousel-meta-label">Title</span>
                            <span className="mc-carousel-meta-value">{carouselFromRepurpose.title}</span>
                        </div>
                        <div className="mc-carousel-meta-item">
                            <span className="mc-carousel-meta-label">Subtitle</span>
                            <span className="mc-carousel-meta-value">{carouselFromRepurpose.subtitle}</span>
                        </div>
                        <div className="mc-carousel-meta-item" style={{ gridColumn: '1 / -1' }}>
                            <span className="mc-carousel-meta-label">Cover Slide</span>
                            <span className="mc-carousel-meta-value">{carouselFromRepurpose.cover_text}</span>
                        </div>
                    </div>

                    <div className="mc-results-section">
                        <h3>{carouselFromRepurpose.slides?.length || 0} Slides</h3>
                        <div className="mc-slide-list">
                            {(carouselFromRepurpose.slides || []).map((slide, i) => (
                                <div key={i} className="mc-slide-card">
                                    <div className="mc-slide-number">{slide.slide_number}</div>
                                    <div className="mc-slide-content">
                                        <div className="mc-slide-headline">{slide.headline}</div>
                                        <div className="mc-slide-body">{slide.body}</div>
                                        {slide.visual_suggestion && (
                                            <div className="mc-slide-visual">
                                                Visual: {slide.visual_suggestion}
                                            </div>
                                        )}
                                    </div>
                                    <CopyButton text={`${slide.headline}\n\n${slide.body}`} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mc-results-section">
                        <h3>Closing CTA Slide</h3>
                        <div className="mc-carousel-meta-item" style={{ width: '100%' }}>
                            <span className="mc-carousel-meta-value">{carouselFromRepurpose.closing_cta}</span>
                        </div>
                    </div>

                    {selectedArticleId && projectId && (
                        <div className="mc-refine-area">
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <GlassButton variant="secondary" onClick={() => setShowRefine(!showRefine)} disabled={regenLoading}>
                                    <Pencil size={14} />
                                    Refine
                                </GlassButton>
                                <GlassButton variant="secondary" className="mc-regen-btn" onClick={handleRegenerate} disabled={regenLoading}>
                                    <RefreshCw size={14} className={regenLoading ? 'mc-spin-icon' : ''} />
                                    {regenLoading ? 'Regenerating...' : 'Regenerate'}
                                </GlassButton>
                            </div>
                            {showRefine && (
                                <div className="mc-form-group" style={{ marginTop: 10 }}>
                                    <textarea
                                        placeholder="Add instructions for regeneration..."
                                        value={regenInstructions}
                                        onChange={e => setRegenInstructions(e.target.value)}
                                        style={{ minHeight: 80 }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </GlassCard>
            )}

            {/* Manual carousel builder form */}
            <GlassCard className="mc-form-card">
                <div className="mc-form-group">
                    <label>Topic *</label>
                    <input
                        type="text"
                        style={{
                            padding: '10px 14px',
                            background: 'var(--panel)',
                            border: '1px solid var(--stroke)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--text)',
                            fontSize: '0.9rem',
                            fontFamily: 'var(--font-family)',
                            outline: 'none',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                        placeholder="e.g. 5 lessons from scaling to $1M ARR"
                        value={topic}
                        onChange={e => setTopic(e.target.value)}
                    />
                </div>
                <div className="mc-form-group">
                    <label>Source Content *</label>
                    <textarea
                        className="tall"
                        placeholder="Paste your article, blog post, or notes here..."
                        value={content}
                        onChange={e => setContent(e.target.value)}
                    />
                </div>
                {error && <ErrorState message={error} />}
                <div className="mc-form-actions">
                    <GlassButton variant="primary" onClick={handleSubmit} disabled={loading}>
                        <Layers size={16} />
                        {loading ? 'Generating Carousel...' : 'Generate Carousel'}
                    </GlassButton>
                </div>
                {loading && <LoadingState message="Building your LinkedIn carousel outline..." />}
            </GlassCard>

            {result && !loading && result.slides && (
                <GlassCard className="mc-results-card">
                    <div className="mc-carousel-meta">
                        <div className="mc-carousel-meta-item">
                            <span className="mc-carousel-meta-label">Title</span>
                            <span className="mc-carousel-meta-value">{result.title}</span>
                        </div>
                        <div className="mc-carousel-meta-item">
                            <span className="mc-carousel-meta-label">Subtitle</span>
                            <span className="mc-carousel-meta-value">{result.subtitle}</span>
                        </div>
                        <div className="mc-carousel-meta-item" style={{ gridColumn: '1 / -1' }}>
                            <span className="mc-carousel-meta-label">Cover Slide</span>
                            <span className="mc-carousel-meta-value">{result.cover_text}</span>
                        </div>
                    </div>

                    <div className="mc-results-section">
                        <h3>{result.slides.length} Slides</h3>
                        <div className="mc-slide-list">
                            {result.slides.map((slide, i) => (
                                <div key={i} className="mc-slide-card">
                                    <div className="mc-slide-number">{slide.slide_number}</div>
                                    <div className="mc-slide-content">
                                        <div className="mc-slide-headline">{slide.headline}</div>
                                        <div className="mc-slide-body">{slide.body}</div>
                                        {slide.visual_suggestion && (
                                            <div className="mc-slide-visual">
                                                Visual: {slide.visual_suggestion}
                                            </div>
                                        )}
                                    </div>
                                    <CopyButton text={`${slide.headline}\n\n${slide.body}`} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mc-results-section">
                        <h3>Closing CTA Slide</h3>
                        <div className="mc-carousel-meta-item" style={{ width: '100%' }}>
                            <span className="mc-carousel-meta-value">{result.closing_cta}</span>
                        </div>
                    </div>
                    {projects && projects.length > 0 && (
                        <SaveToProjectRow
                            projects={projects}
                            defaultProjectId={defaultProjectId}
                            contentType="carousel"
                            contentData={result}
                        />
                    )}
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab: Social Posts (new)
// ---------------------------------------------------------------------------

const SOCIAL_LABELS: Record<string, string> = {
    linkedin: 'LinkedIn',
    twitter: 'Twitter / X',
    instagram: 'Instagram',
    facebook: 'Facebook',
};

// Maps backend social_posts blob keys → social_accounts platform value.
const PLATFORM_KEY_TO_SOCIAL: Record<string, string | null> = {
    linkedin: 'linkedin',
    twitter: 'twitter',
    instagram: 'instagram',
    facebook: 'facebook',
};

// Platforms rendered in the Social Posts grid. Guards both render sites
// against legacy saved data that may include twitter_thread / short_form /
// image_concepts. Note: 'twitter_thread' is intentionally excluded — threads
// surface in the Thread Builder tab, not here.
const RENDERABLE_PLATFORMS = new Set(['linkedin', 'twitter', 'instagram', 'facebook']);

interface SocialAccountOption {
    account_id: string;
    platform: string;
    display_name: string | null;
    username: string | null;
    has_oauth: boolean;
}

// Returns a short relative-time string for a saved_at ISO-8601 UTC timestamp.
// Absent-Z values (naive UTC from the backend) are treated as UTC by appending Z.
function formatRelativeTime(isoString: string): string {
    const raw = isoString.endsWith('Z') || isoString.includes('+') ? isoString : isoString + 'Z';
    const date = new Date(raw);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.max(0, Math.floor(diffMs / 1000)); // clamp negatives to 0
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay <= 30) return `${diffDay}d ago`;
    // > 30 days: show date; include year if > 365 days old
    const opts: Intl.DateTimeFormatOptions = diffDay > 365
        ? { month: 'short', day: 'numeric', year: 'numeric' }
        : { month: 'short', day: 'numeric' };
    return 'on ' + date.toLocaleDateString(undefined, opts);
}

const SocialPostsTab: React.FC<{
    repurposeResult: RepurposeArticleResponse | null;
    onUpdate: (r: RepurposeArticleResponse) => void;
    projectId: string;
    selectedArticleId: string;
    brandGuideId: string;
    onSwitchTab: (tab: string) => void;
    savedTaskRef?: SavedTaskRef;
    onDeleteSaved?: () => void;
}> = ({ repurposeResult, onUpdate, projectId, selectedArticleId, brandGuideId, onSwitchTab, savedTaskRef, onDeleteSaved }) => {
    const [regenLoading, setRegenLoading] = useState<string | null>(null);
    const [refineOpen, setRefineOpen] = useState<string | null>(null);
    const [instructions, setInstructions] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);
    const [editingPlatform, setEditingPlatform] = useState<string | null>(null);
    const [publishingPlatform, setPublishingPlatform] = useState<string | null>(null);
    const [publishedPlatforms, setPublishedPlatforms] = useState<Record<string, boolean>>({});
    const [externalPostUrls, setExternalPostUrls] = useState<Record<string, string>>({});
    const [scheduledPlatforms, setScheduledPlatforms] = useState<Record<string, boolean>>({});
    const [connectedAccounts, setConnectedAccounts] = useState<Record<string, SocialAccountOption[]>>({});

    useEffect(() => {
        if (!projectId) return;
        getSocialAccounts(projectId)
            .then((data: SocialAccountOption[]) => {
                const byPlatform: Record<string, SocialAccountOption[]> = {};
                data.filter((a: SocialAccountOption) => a.has_oauth).forEach((a: SocialAccountOption) => {
                    if (!byPlatform[a.platform]) byPlatform[a.platform] = [];
                    byPlatform[a.platform].push(a);
                });
                setConnectedAccounts(byPlatform);
            })
            .catch(() => setConnectedAccounts({}));
    }, [projectId]);

    // No per-platform seeding needed on mount — savedTaskRef is read directly in getStatusBadge.

    // Hero image state
    const [imagePickerOpen, setImagePickerOpen] = useState(false);
    const [isFetchingImage, setIsFetchingImage] = useState(false);
    const [excludedImageIds, setExcludedImageIds] = useState<number[]>([]);

    // Text overlay state
    const [overlayTarget, setOverlayTarget] = useState<{
        taskId: string;
        baseImageUrl: string;
        currentOverlay: OverlayState | null;
        fallbackPrompt: string;
    } | null>(null);

    if (!repurposeResult?.repurpose_result?.social_posts) {
        return <ContentEmptyState label="social posts" onSwitchTab={onSwitchTab} />;
    }

    const socialPosts = repurposeResult.repurpose_result.social_posts;
    const heroImage = repurposeResult.hero_image;

    const handleRegenerate = async (platform: string, customInstructions?: string): Promise<string> => {
        if (!selectedArticleId || !projectId) return '';
        setRegenLoading(platform);
        setError(null);
        try {
            const resp = await regenerateSection({
                task_id: selectedArticleId,
                project_id: projectId,
                section: `social_${platform}`,
                instructions: customInstructions?.trim() || instructions[platform]?.trim() || undefined,
                brand_guide_id: brandGuideId || undefined,
            });
            if (resp.multichannel) onUpdate(resp.multichannel);
            setInstructions(prev => ({ ...prev, [platform]: '' }));
            setRefineOpen(null);
            // Return the freshly-generated text directly from the response so callers
            // don't have to read from stale closure state.
            return String(resp.multichannel?.repurpose_result?.social_posts?.[platform] || '');
        } catch (err: any) {
            setError(getApiError(err, `Failed to regenerate ${platform} post.`));
            return '';
        } finally {
            setRegenLoading(null);
        }
    };

    const handleDeleteAll = async () => {
        const taskId = repurposeResult?.task_ids?.social_posts || savedTaskRef?.task_id;
        if (!taskId) return;
        if (!window.confirm('Delete the saved social posts for ALL platforms? This cannot be undone.')) return;
        try {
            await deleteTask(taskId);
            if (repurposeResult) {
                const { social_posts: _removed, ...remainingIds } = repurposeResult.task_ids;
                onUpdate({ ...repurposeResult, task_ids: remainingIds });
            }
            onDeleteSaved?.();
        } catch (err: any) {
            setError(getApiError(err, 'Failed to delete social posts.'));
        }
    };

    // Per-card badge: only shows in-session publish/scheduled state; no misleading "Saved" on all cards.
    const getPerCardBadge = (platform: string) => {
        if (publishedPlatforms[platform]) return <GlassBadge variant="info">Published</GlassBadge>;
        if (scheduledPlatforms[platform]) return <GlassBadge variant="warning">Scheduled</GlassBadge>;
        return null;
    };

    // Section-level saved/published indicator (one badge above the grid).
    const getSectionBadge = () => {
        const taskId = repurposeResult?.task_ids?.social_posts || savedTaskRef?.task_id;
        if (!taskId) return null;
        if (savedTaskRef?.external_post_url) return <GlassBadge variant="info">Published</GlassBadge>;
        return <GlassBadge variant="default">Saved</GlassBadge>;
    };

    // Hero image handlers are now delegated to ImagePickerModal

    return (
        <>
            {error && <ErrorState message={error} />}

            {/* Hero Image Section */}
            <GlassCard className="mc-hero-card">
                <div className="mc-section-header" style={{ marginBottom: 12 }}>
                    <div className="mc-section-icon"><Image size={16} /></div>
                    <h3>Hero Image</h3>
                </div>
                {heroImage ? (
                    <div className="mc-hero-manage">
                        <div className="mc-hero-preview">
                            <img
                                src={heroImage.urls?.large2x || heroImage.urls?.large || heroImage.url || heroImage.src || ''}
                                alt={heroImage.description || heroImage.alt || 'Hero image'}
                            />
                            {heroImage.photographer && (
                                <span className="mc-hero-credit">
                                    Photo by {heroImage.photographer}
                                    {heroImage.pexels_url ? (
                                        <> on <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer">Pexels</a></>
                                    ) : null}
                                </span>
                            )}
                        </div>
                        <div className="mc-hero-actions">
                            <GlassButton
                                variant="secondary"
                                onClick={() => setImagePickerOpen(true)}
                                disabled={isFetchingImage || !selectedArticleId}
                            >
                                {isFetchingImage ? <Loader2 size={14} className="mc-spin-icon" /> : <Image size={14} />}
                                {isFetchingImage ? 'Loading...' : 'Change Image'}
                            </GlassButton>
                            {heroImage && (heroImage.urls?.large2x || heroImage.urls?.large || heroImage.url) && selectedArticleId && (
                                <GlassButton
                                    variant="secondary"
                                    onClick={() => setOverlayTarget({
                                        taskId: selectedArticleId,
                                        baseImageUrl: heroImage.urls?.large2x || heroImage.urls?.large || heroImage.url || '',
                                        currentOverlay: heroImage.overlay_state || null,
                                        fallbackPrompt: heroImage.description || heroImage.alt || '',
                                    })}
                                >
                                    <TextIcon size={13} /> Add Overlay
                                </GlassButton>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="mc-hero-empty">
                        <p>No hero image set for this article.</p>
                        <GlassButton
                            variant="secondary"
                            onClick={() => setImagePickerOpen(true)}
                            disabled={isFetchingImage || !selectedArticleId}
                        >
                            {isFetchingImage ? <Loader2 size={14} className="mc-spin-icon" /> : <Image size={14} />}
                            {isFetchingImage ? 'Searching...' : 'Find Hero Image'}
                        </GlassButton>
                    </div>
                )}
            </GlassCard>

            {/* Image picker modal for hero image selection */}
            {imagePickerOpen && selectedArticleId && (
                <ImagePickerModal
                    open={imagePickerOpen}
                    onClose={() => setImagePickerOpen(false)}
                    title="Hero Image"
                    initialPexelsQuery={heroImage?.description || heroImage?.alt || ''}
                    initialAiPrompt={heroImage?.description || heroImage?.alt || ''}
                    aiPlatform="instagram"
                    brandGuideId={brandGuideId || undefined}
                    pexelsFetcher={async (_query, excludedIds) => {
                        // v1: ignores query string — selectImagesForTask uses task's stored image_concept.
                        // Double-write on Pexels path is intentional and idempotent — see spec note.
                        const result = await selectImagesForTask(selectedArticleId, true, [...excludedImageIds, ...excludedIds]);
                        const hero = result.images?.hero;
                        if (!hero) throw new Error('No image returned from Pexels.');
                        const url = hero.urls?.large2x || hero.urls?.large || hero.url || hero.src || '';
                        return { image_url: url, pexels_id: hero.pexels_id, photographer: hero.photographer };
                    }}
                    onImageSelected={async (url, meta) => {
                        setIsFetchingImage(true);
                        try {
                            const result = await updateHeroImage(selectedArticleId, url, meta.photographer || '', meta.source);
                            if (result.hero) onUpdate({ ...repurposeResult!, hero_image: result.hero });
                            if (meta.source === 'pexels' && meta.pexels_id) {
                                setExcludedImageIds(prev => [...prev, meta.pexels_id!]);
                            }
                        } finally {
                            setIsFetchingImage(false);
                        }
                    }}
                />
            )}

            {/* Platform Posts — section header with status + Delete All */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>Platform Posts</span>
                    {getSectionBadge()}
                    {savedTaskRef?.saved_at && (
                        <span
                            style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 400 }}
                            title={`Saved ${new Date(savedTaskRef.saved_at.endsWith('Z') || savedTaskRef.saved_at.includes('+') ? savedTaskRef.saved_at : savedTaskRef.saved_at + 'Z').toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} UTC`}
                        >
                            Saved {formatRelativeTime(savedTaskRef.saved_at)}
                        </span>
                    )}
                </div>
                {(repurposeResult?.task_ids?.social_posts || savedTaskRef?.task_id) && (
                    <GlassButton variant="danger" onClick={handleDeleteAll}>
                        <Trash2 size={14} />
                        Delete All
                    </GlassButton>
                )}
            </div>
            <div className="assets-grid">
                {Object.entries(socialPosts).filter(([platform]) => RENDERABLE_PLATFORMS.has(platform)).map(([platform, text]) => (
                    <GlassCard key={platform} className="asset-card">
                        <div className="asset-card-header">
                            <div className="asset-card-type">
                                <span className={`mc-platform-icon ${getPlatformIconClass(platform)}`}>
                                    {getPlatformLabel(platform)}
                                </span>
                                {SOCIAL_LABELS[platform] || platform.replace(/_/g, ' ')}
                            </div>
                            {getPerCardBadge(platform)}
                        </div>
                        {heroImage && (
                            <img
                                className="asset-card-image"
                                src={heroImage.urls?.large2x || heroImage.urls?.large || heroImage.url || heroImage.src || ''}
                                alt="Social post image"
                            />
                        )}
                        <p className="asset-card-preview">
                            {String(text).slice(0, 200)}{String(text).length > 200 ? '…' : ''}
                        </p>
                        {selectedArticleId && projectId && (
                            <div className="asset-card-actions">
                                <div className="asset-card-actions-row">
                                    <GlassButton
                                        variant="secondary"
                                        onClick={() => setEditingPlatform(platform)}
                                    >
                                        <Pencil size={14} />
                                        Edit
                                    </GlassButton>
                                    <CopyButton text={String(text)} />
                                </div>
                                {/* Regenerate is disabled for twitter: section 'social_twitter'
                                    targets the thread key, not the single-tweet key. */}
                                {platform !== 'twitter' && (
                                    <div className="asset-card-actions-row">
                                        <GlassButton
                                            variant="secondary"
                                            onClick={() => handleRegenerate(platform)}
                                            disabled={regenLoading === platform}
                                        >
                                            <RefreshCw size={14} className={regenLoading === platform ? 'mc-spin-icon' : ''} />
                                            {regenLoading === platform ? 'Regenerating...' : 'Regenerate'}
                                        </GlassButton>
                                    </div>
                                )}
                                {(() => {
                                    const mappedPlatform = PLATFORM_KEY_TO_SOCIAL[platform];
                                    const taskId = repurposeResult?.task_ids?.social_posts;
                                    if (!mappedPlatform || !taskId) return null;
                                    if (publishedPlatforms[platform] && externalPostUrls[platform]) {
                                        return (
                                            <div className="asset-card-actions-row">
                                                <a
                                                    href={externalPostUrls[platform]}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ fontSize: '0.8rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 4 }}
                                                >
                                                    <ExternalLink size={12} /> View live
                                                </a>
                                            </div>
                                        );
                                    }
                                    if (scheduledPlatforms[platform]) return null;
                                    const hasAccount = (connectedAccounts[mappedPlatform] || []).length > 0;
                                    return (
                                        <div className="asset-card-actions-row">
                                            <GlassButton
                                                variant="primary"
                                                onClick={() => setPublishingPlatform(platform)}
                                                disabled={!hasAccount}
                                                title={!hasAccount ? `Connect a ${SOCIAL_LABELS[platform] || platform} account in Social Accounts to publish from here.` : undefined}
                                            >
                                                <Send size={14} />
                                                Publish Now
                                            </GlassButton>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </GlassCard>
                ))}
            </div>

            {publishingPlatform && repurposeResult?.task_ids?.social_posts && (
                <SocialPublishModal
                    taskId={repurposeResult.task_ids.social_posts}
                    projectId={projectId}
                    platformContent={{
                        [PLATFORM_KEY_TO_SOCIAL[publishingPlatform] as string]: {
                            post: socialPosts[publishingPlatform],
                        },
                    }}
                    platformFilter={PLATFORM_KEY_TO_SOCIAL[publishingPlatform] as string}
                    onClose={() => setPublishingPlatform(null)}
                    onSuccess={(postUrl) => {
                        if (postUrl) {
                            setExternalPostUrls(prev => ({ ...prev, [publishingPlatform!]: postUrl }));
                            setPublishedPlatforms(prev => ({ ...prev, [publishingPlatform!]: true }));
                        } else {
                            setScheduledPlatforms(prev => ({ ...prev, [publishingPlatform!]: true }));
                        }
                        setPublishingPlatform(null);
                    }}
                />
            )}

            {editingPlatform && socialPosts[editingPlatform] !== undefined && (
                <SocialAssetEditor
                    platform={editingPlatform}
                    initialContent={String(socialPosts[editingPlatform])}
                    initialImageUrl={heroImage ? (heroImage.urls?.large || heroImage.url || heroImage.src || '') : undefined}
                    imageEditable={false}
                    onSave={async ({ content }) => {
                        // Build the new shape ONCE so both the optimistic UI
                        // update and the backend write stay in sync.
                        const previousRepurposeResult = repurposeResult;
                        const newRepurposeResult = {
                            ...repurposeResult!,
                            repurpose_result: {
                                ...repurposeResult!.repurpose_result,
                                social_posts: {
                                    ...repurposeResult!.repurpose_result.social_posts,
                                    [editingPlatform]: String(content),
                                },
                            },
                        };
                        // Optimistic — show the edit immediately, revert on failure.
                        onUpdate(newRepurposeResult);
                        // Persist to backend. Without this, edits only lived in
                        // React state and were lost on tab/module navigation.
                        // IMPORTANT: write to the SOURCE article task (selectedArticleId)
                        // because the loader (multichannel_routes:get_article_multichannel_content)
                        // reads from task.output_data.multichannel on the article task. The
                        // saved-child task (task_ids.social_posts / savedTaskRef.task_id) is a
                        // separate catalogue snapshot whose synthesized_result holds the saved
                        // copy; writing edits there leaves the article task stale and the next
                        // load shows the original content. Same pattern as the overlay handler
                        // below which uses taskId: selectedArticleId.
                        const taskId = selectedArticleId;
                        if (!taskId) return;
                        try {
                            const task = await getTask(taskId);
                            const updatedOutputData = { ...(task.output_data || {}) };
                            const multichannel = { ...(updatedOutputData.multichannel || {}) };
                            multichannel.repurpose_result = newRepurposeResult.repurpose_result;
                            updatedOutputData.multichannel = multichannel;
                            await updateTask(taskId, { output_data: updatedOutputData });
                        } catch (e: any) {
                            onUpdate(previousRepurposeResult!);  // revert
                            alert(getApiError(e, 'Failed to save social post — please try again.'));
                        }
                    }}
                    onClose={() => setEditingPlatform(null)}
                    regenerate={{
                        onRegenerate: async (instructions: string) => {
                            // handleRegenerate returns the new text directly from the API
                            // response, avoiding a stale-closure read of repurposeResult.
                            return await handleRegenerate(editingPlatform, instructions);
                        },
                        loading: regenLoading === editingPlatform,
                    }}
                />
            )}

            {overlayTarget && (
                <TextOverlayModal
                    baseImageUrl={
                        // On re-edit, use the stored original base (pre-text) image so we
                        // always composite against the clean original, not the composited output.
                        overlayTarget.currentOverlay?.base_image_url ||
                        overlayTarget.baseImageUrl
                    }
                    initialOverlayState={overlayTarget.currentOverlay}
                    fallbackVisualPrompt={overlayTarget.fallbackPrompt || undefined}
                    projectId={projectId}
                    onClose={() => setOverlayTarget(null)}
                    onApplied={async ({ url, overlay_state }: { url: string; overlay_state: OverlayState }) => {
                        const previousRepurposeResult = repurposeResult;
                        const newHeroImage = {
                            ...(repurposeResult?.hero_image || {}),
                            urls: {
                                ...(repurposeResult?.hero_image?.urls || {}),
                                large2x: url, large: url, original: url,
                            },
                            url, overlay_state,
                        };
                        // Optimistic update — revert on failure
                        onUpdate({ ...(repurposeResult as any), hero_image: newHeroImage });
                        try {
                            const task = await getTask(overlayTarget.taskId);
                            const updatedOutputData = { ...(task.output_data || {}) };
                            const multichannel = { ...(updatedOutputData.multichannel || {}) };
                            multichannel.hero_image = newHeroImage;
                            updatedOutputData.multichannel = multichannel;
                            await updateTask(overlayTarget.taskId, { output_data: updatedOutputData });
                        } catch (e: any) {
                            onUpdate({ ...(previousRepurposeResult as any) }); // revert optimistic update
                            alert(getApiError(e, 'Failed to persist overlay — please try again.'));
                        }
                        setOverlayTarget(null);
                    }}
                />
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab: Video Script (new)
// ---------------------------------------------------------------------------

const VideoScriptTab: React.FC<{
    repurposeResult: RepurposeArticleResponse | null;
    onUpdate: (r: RepurposeArticleResponse) => void;
    projectId: string;
    selectedArticleId: string;
    brandGuideId: string;
    onSwitchTab: (tab: string) => void;
}> = ({ repurposeResult, onUpdate, projectId, selectedArticleId, brandGuideId, onSwitchTab }) => {
    const [regenLoading, setRegenLoading] = useState(false);
    const [showRefine, setShowRefine] = useState(false);
    const [regenInstructions, setRegenInstructions] = useState('');
    const [error, setError] = useState<string | null>(null);

    if (!repurposeResult?.repurpose_result?.video_script) {
        return <ContentEmptyState label="video script content" onSwitchTab={onSwitchTab} />;
    }

    const vs = repurposeResult.repurpose_result.video_script;

    const handleRegenerate = async () => {
        if (!selectedArticleId || !projectId) return;
        setRegenLoading(true);
        setError(null);
        try {
            const resp = await regenerateSection({
                task_id: selectedArticleId,
                project_id: projectId,
                section: 'video_script',
                instructions: regenInstructions.trim() || undefined,
                brand_guide_id: brandGuideId || undefined,
            });
            if (resp.multichannel) onUpdate(resp.multichannel);
            setRegenInstructions('');
            setShowRefine(false);
        } catch (err: any) {
            setError(getApiError(err, 'Regeneration failed.'));
        } finally {
            setRegenLoading(false);
        }
    };

    const fullScript = [vs.hook, ...(vs.talking_points || []), vs.cta].join('\n\n');

    return (
        <GlassCard className="mc-results-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div className="mc-section-header" style={{ marginBottom: 0 }}>
                    <div className="mc-section-icon"><Video size={16} /></div>
                    <h3>Short-Form Video Script (~60 sec)</h3>
                </div>
                <CopyButton text={fullScript} label="Copy All" />
            </div>

            {error && <ErrorState message={error} />}

            <div className="mc-video-script">
                <div className="mc-script-field">
                    <span className="mc-script-label">Hook (0-5 seconds)</span>
                    <div className="mc-script-content" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                        <span style={{ flex: 1 }}>{vs.hook}</span>
                        <CopyButton text={vs.hook} />
                    </div>
                </div>
                {vs.talking_points && vs.talking_points.length > 0 && (
                    <div className="mc-script-field">
                        <span className="mc-script-label">Talking Points</span>
                        <ul className="mc-talking-points">
                            {vs.talking_points.map((point, i) => (
                                <li key={i} data-num={`${i + 1}`}>{point}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="mc-script-field">
                    <span className="mc-script-label">CTA (closing)</span>
                    <div className="mc-script-content" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                        <span style={{ flex: 1 }}>{vs.cta}</span>
                        <CopyButton text={vs.cta} />
                    </div>
                </div>
            </div>

            {selectedArticleId && projectId && (
                <div className="mc-refine-area" style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <GlassButton variant="secondary" onClick={() => setShowRefine(!showRefine)} disabled={regenLoading}>
                            <Pencil size={14} />
                            Refine
                        </GlassButton>
                        <GlassButton variant="secondary" className="mc-regen-btn" onClick={handleRegenerate} disabled={regenLoading}>
                            <RefreshCw size={14} className={regenLoading ? 'mc-spin-icon' : ''} />
                            {regenLoading ? 'Regenerating...' : 'Regenerate'}
                        </GlassButton>
                    </div>
                    {showRefine && (
                        <div className="mc-form-group" style={{ marginTop: 10 }}>
                            <textarea
                                placeholder="Add instructions for regeneration (e.g. make the hook more attention-grabbing, add more urgency)..."
                                value={regenInstructions}
                                onChange={e => setRegenInstructions(e.target.value)}
                                style={{ minHeight: 80 }}
                            />
                        </div>
                    )}
                </div>
            )}
        </GlassCard>
    );
};

// ---------------------------------------------------------------------------
// Tab: Email Sequence (new)
// ---------------------------------------------------------------------------

const EmailSequenceTab: React.FC<{
    repurposeResult: RepurposeArticleResponse | null;
    onUpdate: (r: RepurposeArticleResponse) => void;
    projectId: string;
    selectedArticleId: string;
    brandGuideId: string;
    onSwitchTab: (tab: string) => void;
}> = ({ repurposeResult, onUpdate, projectId, selectedArticleId, brandGuideId, onSwitchTab }) => {
    const [regenLoading, setRegenLoading] = useState(false);
    const [showRefine, setShowRefine] = useState(false);
    const [regenInstructions, setRegenInstructions] = useState('');
    const [error, setError] = useState<string | null>(null);

    if (!repurposeResult?.email_drip?.emails || repurposeResult.email_drip.emails.length === 0) {
        return <ContentEmptyState label="email sequence content" onSwitchTab={onSwitchTab} />;
    }

    const drip = repurposeResult.email_drip;

    const handleRegenerate = async () => {
        if (!selectedArticleId || !projectId) return;
        setRegenLoading(true);
        setError(null);
        try {
            const resp = await regenerateSection({
                task_id: selectedArticleId,
                project_id: projectId,
                section: 'email_drip',
                instructions: regenInstructions.trim() || undefined,
                brand_guide_id: brandGuideId || undefined,
            });
            if (resp.multichannel) onUpdate(resp.multichannel);
            setRegenInstructions('');
            setShowRefine(false);
        } catch (err: any) {
            setError(getApiError(err, 'Regeneration failed.'));
        } finally {
            setRegenLoading(false);
        }
    };

    return (
        <GlassCard className="mc-results-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div className="mc-section-header" style={{ marginBottom: 0 }}>
                    <div className="mc-section-icon"><Mail size={16} /></div>
                    <h3>Email Drip Sequence — {drip.sequence_name || `${drip.emails.length} emails`}</h3>
                </div>
            </div>

            {error && <ErrorState message={error} />}

            <div className="mc-drip-cards">
                {drip.emails.map((email, i) => (
                    <div className="mc-drip-card" key={i}>
                        <div className="mc-drip-card-header">
                            <span className="mc-drip-step">Step {email.step_order}</span>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <span className="mc-drip-delay">Day {email.delay_days}</span>
                                <CopyButton text={`Subject: ${email.subject_line}\n\n${email.body_plain || email.body_html}`} />
                            </div>
                        </div>
                        <div className="mc-drip-subject">
                            <strong>Subject:</strong> {email.subject_line}
                        </div>
                        <div className="mc-drip-preview">
                            <strong>Preview:</strong> {email.preview_text}
                        </div>
                        <div className="mc-drip-body" dangerouslySetInnerHTML={{ __html: email.body_html }} />
                    </div>
                ))}
            </div>

            {selectedArticleId && projectId && (
                <div className="mc-refine-area" style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <GlassButton variant="secondary" onClick={() => setShowRefine(!showRefine)} disabled={regenLoading}>
                            <Pencil size={14} />
                            Refine
                        </GlassButton>
                        <GlassButton variant="secondary" className="mc-regen-btn" onClick={handleRegenerate} disabled={regenLoading}>
                            <RefreshCw size={14} className={regenLoading ? 'mc-spin-icon' : ''} />
                            {regenLoading ? 'Regenerating...' : 'Regenerate'}
                        </GlassButton>
                    </div>
                    {showRefine && (
                        <div className="mc-form-group" style={{ marginTop: 10 }}>
                            <textarea
                                placeholder="Add instructions for regeneration (e.g. make the tone more casual, add a discount offer in email 2)..."
                                value={regenInstructions}
                                onChange={e => setRegenInstructions(e.target.value)}
                                style={{ minHeight: 80 }}
                            />
                        </div>
                    )}
                </div>
            )}
        </GlassCard>
    );
};

// ---------------------------------------------------------------------------
// Tab: Ad Copy
// ---------------------------------------------------------------------------

const AdCopyTab: React.FC<{ brandGuides: BrandGuide[]; projects?: Project[]; defaultProjectId?: string; brandGuideId: string }> = ({ brandGuides, projects, defaultProjectId, brandGuideId: parentBrandGuideId }) => {
    const [productService, setProductService] = useState('');
    const [targetAudience, setTargetAudience] = useState('');
    const [goal, setGoal] = useState('');
    const [selectedAdPlatforms, setSelectedAdPlatforms] = useState<string[]>(['google_search', 'meta_facebook', 'linkedin_sponsored']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<AdCopyResult | null>(null);

    const handleSubmit = async () => {
        if (!productService.trim()) { setError('Product/service is required.'); return; }
        if (!targetAudience.trim()) { setError('Target audience is required.'); return; }
        if (!goal.trim()) { setError('Campaign goal is required.'); return; }
        if (selectedAdPlatforms.length === 0) { setError('Select at least one platform.'); return; }
        setError(null);
        setLoading(true);
        try {
            const data = await generateAdCopy({
                product_service: productService.trim(),
                target_audience: targetAudience.trim(),
                goal: goal.trim(),
                brand_guide_id: parentBrandGuideId || undefined,
                platforms: selectedAdPlatforms,
            });
            setResult(data);
        } catch (err: any) {
            setError(getApiError(err, 'Ad copy generation failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <GlassCard className="mc-form-card">
                <div className="mc-form-grid">
                    <div className="mc-form-group">
                        <label>Product / Service *</label>
                        <input
                            type="text"
                            style={{
                                padding: '10px 14px',
                                background: 'var(--panel)',
                                border: '1px solid var(--stroke)',
                                borderRadius: 'var(--radius-md)',
                                color: 'var(--text)',
                                fontSize: '0.9rem',
                                fontFamily: 'var(--font-family)',
                                outline: 'none',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                            placeholder="e.g. AI marketing platform for SMBs"
                            value={productService}
                            onChange={e => setProductService(e.target.value)}
                        />
                    </div>
                    <div className="mc-form-group">
                        <label>Target Audience *</label>
                        <input
                            type="text"
                            style={{
                                padding: '10px 14px',
                                background: 'var(--panel)',
                                border: '1px solid var(--stroke)',
                                borderRadius: 'var(--radius-md)',
                                color: 'var(--text)',
                                fontSize: '0.9rem',
                                fontFamily: 'var(--font-family)',
                                outline: 'none',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                            placeholder="e.g. marketing managers at B2B SaaS companies"
                            value={targetAudience}
                            onChange={e => setTargetAudience(e.target.value)}
                        />
                    </div>
                </div>
                <div className="mc-form-group">
                    <label>Campaign Goal *</label>
                    <input
                        type="text"
                        style={{
                            padding: '10px 14px',
                            background: 'var(--panel)',
                            border: '1px solid var(--stroke)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--text)',
                            fontSize: '0.9rem',
                            fontFamily: 'var(--font-family)',
                            outline: 'none',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                        placeholder="e.g. drive free trial signups, increase demo bookings"
                        value={goal}
                        onChange={e => setGoal(e.target.value)}
                    />
                </div>
                <div className="mc-form-group">
                    <label>Platforms *</label>
                    <PlatformCheckboxGroup
                        options={AD_PLATFORMS}
                        selected={selectedAdPlatforms}
                        onChange={setSelectedAdPlatforms}
                    />
                </div>
                {error && <ErrorState message={error} />}
                <div className="mc-form-actions">
                    <GlassButton variant="primary" onClick={handleSubmit} disabled={loading}>
                        <Megaphone size={16} />
                        {loading ? 'Generating Ads...' : 'Generate Ad Copy'}
                    </GlassButton>
                </div>
                {loading && <LoadingState message="Generating platform-specific ad copy..." />}
            </GlassCard>

            {result && !loading && (
                <GlassCard className="mc-results-card">
                    <div className="mc-ad-platforms">

                        {/* Google Search */}
                        {result.google_search && (
                            <div className="mc-ad-platform-card">
                                <div className="mc-ad-platform-header">
                                    <span className="mc-ad-platform-badge google">Google Search</span>
                                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                                        Responsive Search Ad
                                    </span>
                                </div>

                                <div className="mc-ad-field">
                                    <span className="mc-ad-field-label">
                                        Headlines ({result.google_search.headlines?.length || 0})
                                        <span className="mc-ad-char-limit">max 30 chars each</span>
                                    </span>
                                    <div className="mc-ad-headlines">
                                        {(result.google_search.headlines || []).map((h: string, i: number) => (
                                            <div key={i} className="mc-ad-headline-item">
                                                <span style={{ flex: 1, wordBreak: 'break-word' }}>{h}</span>
                                                <span className={`mc-ad-headline-len ${h.length > 30 ? 'over' : 'ok'}`}>
                                                    {h.length}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mc-ad-field">
                                    <span className="mc-ad-field-label">
                                        Descriptions ({result.google_search.descriptions?.length || 0})
                                        <span className="mc-ad-char-limit">max 90 chars each</span>
                                    </span>
                                    {(result.google_search.descriptions || []).map((d: string, i: number) => (
                                        <div key={i} className="mc-ad-text-item">
                                            <span className="mc-ad-text-item-text">{d}</span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                                <span className={`mc-ad-headline-len ${d.length > 90 ? 'over' : 'ok'}`}>
                                                    {d.length}
                                                </span>
                                                <CopyButton text={d} />
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {result.google_search.display_path && (
                                    <div className="mc-ad-field">
                                        <span className="mc-ad-field-label">Display Path</span>
                                        <div className="mc-ad-text-item">
                                            <span className="mc-ad-text-item-text">{result.google_search.display_path}</span>
                                            <CopyButton text={result.google_search.display_path} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Meta / Facebook */}
                        {result.meta_facebook && (
                            <div className="mc-ad-platform-card">
                                <div className="mc-ad-platform-header">
                                    <span className="mc-ad-platform-badge meta">Meta / Facebook</span>
                                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                                        Feed Ad
                                    </span>
                                </div>

                                <div className="mc-ad-field">
                                    <span className="mc-ad-field-label">
                                        Primary Text
                                        <span className="mc-ad-char-limit">125 chars recommended</span>
                                    </span>
                                    <div className="mc-ad-text-item">
                                        <span className="mc-ad-text-item-text">{result.meta_facebook.primary_text}</span>
                                        <CopyButton text={result.meta_facebook.primary_text} />
                                    </div>
                                </div>

                                <div className="mc-ad-field">
                                    <span className="mc-ad-field-label">
                                        Headline
                                        <span className="mc-ad-char-limit">max 40 chars</span>
                                    </span>
                                    <div className="mc-ad-text-item">
                                        <span className="mc-ad-text-item-text">{result.meta_facebook.headline}</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                            <span className={`mc-ad-headline-len ${result.meta_facebook.headline?.length > 40 ? 'over' : 'ok'}`}>
                                                {result.meta_facebook.headline?.length || 0}
                                            </span>
                                            <CopyButton text={result.meta_facebook.headline} />
                                        </span>
                                    </div>
                                </div>

                                <div className="mc-ad-field">
                                    <span className="mc-ad-field-label">
                                        Description
                                        <span className="mc-ad-char-limit">max 25 chars</span>
                                    </span>
                                    <div className="mc-ad-text-item">
                                        <span className="mc-ad-text-item-text">{result.meta_facebook.description}</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                            <span className={`mc-ad-headline-len ${result.meta_facebook.description?.length > 25 ? 'over' : 'ok'}`}>
                                                {result.meta_facebook.description?.length || 0}
                                            </span>
                                            <CopyButton text={result.meta_facebook.description} />
                                        </span>
                                    </div>
                                </div>

                                {result.meta_facebook.cta_options && result.meta_facebook.cta_options.length > 0 && (
                                    <div className="mc-ad-field">
                                        <span className="mc-ad-field-label">CTA Button Options</span>
                                        <div className="mc-ad-cta-list">
                                            {result.meta_facebook.cta_options.map((cta: string, i: number) => (
                                                <span key={i} className="mc-ad-cta-tag">{cta}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* LinkedIn Sponsored */}
                        {result.linkedin_sponsored && (
                            <div className="mc-ad-platform-card">
                                <div className="mc-ad-platform-header">
                                    <span className="mc-ad-platform-badge linkedin">LinkedIn Sponsored</span>
                                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                                        Single Image Ad
                                    </span>
                                </div>

                                <div className="mc-ad-field">
                                    <span className="mc-ad-field-label">
                                        Intro Text
                                        <span className="mc-ad-char-limit">max 150 chars</span>
                                    </span>
                                    <div className="mc-ad-text-item">
                                        <span className="mc-ad-text-item-text">{result.linkedin_sponsored.intro_text}</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                            <span className={`mc-ad-headline-len ${result.linkedin_sponsored.intro_text?.length > 150 ? 'over' : 'ok'}`}>
                                                {result.linkedin_sponsored.intro_text?.length || 0}
                                            </span>
                                            <CopyButton text={result.linkedin_sponsored.intro_text} />
                                        </span>
                                    </div>
                                </div>

                                <div className="mc-ad-field">
                                    <span className="mc-ad-field-label">
                                        Headline
                                        <span className="mc-ad-char-limit">max 70 chars</span>
                                    </span>
                                    <div className="mc-ad-text-item">
                                        <span className="mc-ad-text-item-text">{result.linkedin_sponsored.headline}</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                            <span className={`mc-ad-headline-len ${result.linkedin_sponsored.headline?.length > 70 ? 'over' : 'ok'}`}>
                                                {result.linkedin_sponsored.headline?.length || 0}
                                            </span>
                                            <CopyButton text={result.linkedin_sponsored.headline} />
                                        </span>
                                    </div>
                                </div>

                                <div className="mc-ad-field">
                                    <span className="mc-ad-field-label">
                                        Description
                                        <span className="mc-ad-char-limit">max 100 chars</span>
                                    </span>
                                    <div className="mc-ad-text-item">
                                        <span className="mc-ad-text-item-text">{result.linkedin_sponsored.description}</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                            <span className={`mc-ad-headline-len ${result.linkedin_sponsored.description?.length > 100 ? 'over' : 'ok'}`}>
                                                {result.linkedin_sponsored.description?.length || 0}
                                            </span>
                                            <CopyButton text={result.linkedin_sponsored.description} />
                                        </span>
                                    </div>
                                </div>

                                <div className="mc-ad-field">
                                    <span className="mc-ad-field-label">CTA Button</span>
                                    <div className="mc-ad-cta-list">
                                        <span className="mc-ad-cta-tag">{result.linkedin_sponsored.cta}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                    {projects && projects.length > 0 && (
                        <SaveToProjectRow
                            projects={projects}
                            defaultProjectId={defaultProjectId}
                            contentType="ad_copy"
                            contentData={result}
                        />
                    )}
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab: UTM Builder
// ---------------------------------------------------------------------------

const UTM_SOURCE_OPTIONS = [
    { value: 'google', label: 'Google' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'email', label: 'Email' },
    { value: 'twitter', label: 'Twitter / X' },
    { value: 'other', label: 'Other' },
];

const UTM_MEDIUM_OPTIONS = [
    { value: 'cpc', label: 'CPC / Paid' },
    { value: 'email', label: 'Email' },
    { value: 'social', label: 'Social' },
    { value: 'organic', label: 'Organic' },
    { value: 'referral', label: 'Referral' },
    { value: 'other', label: 'Other' },
];

const UtmBuilderTab: React.FC<{ projectId: string }> = ({ projectId }) => {
    const [baseUrl, setBaseUrl] = useState('');
    const [source, setSource] = useState('google');
    const [medium, setMedium] = useState('cpc');
    const [campaignName, setCampaignName] = useState('');
    const [content, setContent] = useState('');
    const [term, setTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<{ tagged_url: string; params: any; utm_link_id?: string } | null>(null);
    const [copied, setCopied] = useState(false);
    const [history, setHistory] = useState<UtmLinkRow[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [rowCopiedId, setRowCopiedId] = useState<string | null>(null);
    const [recentlyAdded, setRecentlyAdded] = useState<string | null>(null);

    const refreshHistory = React.useCallback(async () => {
        if (!projectId) { setHistory([]); return; }
        setHistoryLoading(true);
        try {
            const rows = await listUtmLinks(projectId, true);
            setHistory(rows);
        } catch {
            setHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [projectId]);

    React.useEffect(() => { refreshHistory(); }, [refreshHistory]);

    const handleBuild = async () => {
        if (!projectId) { setError('Select a project first.'); return; }
        if (!baseUrl.trim()) { setError('Base URL is required.'); return; }
        if (!campaignName.trim()) { setError('Campaign name is required.'); return; }
        setError(null);
        setLoading(true);
        try {
            const data = await buildUtmUrl({
                base_url: baseUrl.trim(),
                source,
                medium,
                campaign_name: campaignName.trim(),
                content: content.trim() || undefined,
                term: term.trim() || undefined,
                project_id: projectId,
            });
            setResult(data);
            if (data.utm_link_id) setRecentlyAdded(data.utm_link_id);
            await refreshHistory();
            // Clear the highlight after 3s
            setTimeout(() => setRecentlyAdded(null), 3000);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to build UTM URL. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleRowCopy = async (row: UtmLinkRow) => {
        try {
            await navigator.clipboard.writeText(row.tagged_url);
            setRowCopiedId(row.utm_link_id);
            setTimeout(() => setRowCopiedId(null), 1500);
        } catch { /* ignore */ }
    };

    const handleRowDelete = async (row: UtmLinkRow) => {
        if (!window.confirm(`Delete UTM link for "${row.campaign}" (${row.source}/${row.medium})?\n\nThe link will be hidden from history; click counts in analytics are preserved.\nRebuilding the same combination will restore it.`)) return;
        try {
            await deleteUtmLink(row.utm_link_id);
            await refreshHistory();
        } catch (err: any) {
            alert(getApiError(err, 'Failed to delete UTM link.'));
        }
    };

    const handleCopy = async () => {
        if (!result) return;
        try {
            await navigator.clipboard.writeText(result.tagged_url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* ignore */ }
    };

    return (
        <>
            <GlassCard className="mc-form-card">
                <div className="mc-form-group">
                    <label>Base URL *</label>
                    <input
                        type="url"
                        className="mc-text-input"
                        style={{ padding: '10px 14px', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', color: 'var(--text)', fontSize: '0.9rem', fontFamily: 'var(--font-family)', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                        placeholder="https://example.com/landing-page"
                        value={baseUrl}
                        onChange={e => setBaseUrl(e.target.value)}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                    <div className="mc-form-group">
                        <label>Source *</label>
                        <GlassSelect options={UTM_SOURCE_OPTIONS} value={source} onChange={setSource} />
                    </div>
                    <div className="mc-form-group">
                        <label>Medium *</label>
                        <GlassSelect options={UTM_MEDIUM_OPTIONS} value={medium} onChange={setMedium} />
                    </div>
                </div>

                <div className="mc-form-group">
                    <label>Campaign Name *</label>
                    <input
                        type="text"
                        className="mc-text-input"
                        style={{ padding: '10px 14px', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', color: 'var(--text)', fontSize: '0.9rem', fontFamily: 'var(--font-family)', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                        placeholder="e.g. spring-sale-2026"
                        value={campaignName}
                        onChange={e => setCampaignName(e.target.value)}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                    <div className="mc-form-group">
                        <label>Content (optional)</label>
                        <input
                            type="text"
                            className="mc-text-input"
                            style={{ padding: '10px 14px', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', color: 'var(--text)', fontSize: '0.9rem', fontFamily: 'var(--font-family)', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                            placeholder="e.g. banner-ad-v1"
                            value={content}
                            onChange={e => setContent(e.target.value)}
                        />
                    </div>
                    <div className="mc-form-group">
                        <label>Term (optional)</label>
                        <input
                            type="text"
                            className="mc-text-input"
                            style={{ padding: '10px 14px', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', color: 'var(--text)', fontSize: '0.9rem', fontFamily: 'var(--font-family)', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                            placeholder="e.g. running shoes"
                            value={term}
                            onChange={e => setTerm(e.target.value)}
                        />
                    </div>
                </div>

                {error && <ErrorState message={error} />}
                <div className="mc-form-actions">
                    <GlassButton variant="primary" onClick={handleBuild} disabled={loading}>
                        <Link size={16} />
                        {loading ? 'Building...' : 'Build UTM URL'}
                    </GlassButton>
                </div>
            </GlassCard>

            {result && !loading && (
                <GlassCard className="mc-results-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                        <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                            Tagged URL
                        </h3>
                        <button
                            className={`mc-copy-btn ${copied ? 'copied' : ''}`}
                            onClick={handleCopy}
                        >
                            {copied ? <Check size={12} /> : <Copy size={12} />}
                            {copied ? 'Copied' : 'Copy URL'}
                        </button>
                    </div>

                    <div style={{ background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)', fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--text)', wordBreak: 'break-all', marginBottom: 'var(--space-4)' }}>
                        {result.tagged_url}
                    </div>

                    <h4 style={{ margin: '0 0 var(--space-3) 0', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        UTM Parameters
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>Parameter</th>
                                <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(result.params).map(([key, value]) => (
                                <tr key={key}>
                                    <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--primary-500)', borderBottom: '1px solid var(--divider)' }}>{key}</td>
                                    <td style={{ padding: '8px', fontSize: 'var(--text-sm)', color: 'var(--text)', borderBottom: '1px solid var(--divider)' }}>{String(value)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </GlassCard>
            )}

            {/* ── UTM history table ─────────────────────────────────────── */}
            <GlassCard className="mc-results-card" style={{ marginTop: 'var(--space-4)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                    <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                        UTM history{history.length > 0 && <span style={{ marginLeft: 8, fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'var(--font-normal)' }}>({history.length})</span>}
                    </h3>
                    <button
                        className="mc-copy-btn"
                        onClick={refreshHistory}
                        disabled={historyLoading}
                        title="Refresh"
                    >
                        <RefreshCw size={12} className={historyLoading ? 'mc-spin-icon' : ''} />
                        Refresh
                    </button>
                </div>
                {!projectId ? (
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
                        Select a project above to see its UTM history.
                    </p>
                ) : historyLoading && history.length === 0 ? (
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>Loading…</p>
                ) : history.length === 0 ? (
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
                        Build your first UTM URL above and it'll be saved here for re-use.
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>Campaign</th>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>Source / Medium</th>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>Content</th>
                                    <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>Clicks</th>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>Created</th>
                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--divider)' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map(row => (
                                    <tr
                                        key={row.utm_link_id}
                                        style={{
                                            background: recentlyAdded === row.utm_link_id ? 'rgba(33,119,255,0.08)' : undefined,
                                            transition: 'background 0.6s ease',
                                        }}
                                    >
                                        <td style={{ padding: '8px', color: 'var(--text)', borderBottom: '1px solid var(--divider)' }}>
                                            <div style={{ fontWeight: 600 }}>{row.campaign}</div>
                                            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-secondary)', wordBreak: 'break-all', marginTop: 2 }} title={row.tagged_url}>
                                                {row.tagged_url.length > 80 ? row.tagged_url.slice(0, 80) + '…' : row.tagged_url}
                                            </div>
                                        </td>
                                        <td style={{ padding: '8px', color: 'var(--text)', borderBottom: '1px solid var(--divider)' }}>
                                            <span style={{ color: 'var(--primary-500)', fontFamily: 'monospace' }}>{row.source}</span>
                                            <span style={{ color: 'var(--text-secondary)' }}> / </span>
                                            <span style={{ color: 'var(--text)', fontFamily: 'monospace' }}>{row.medium}</span>
                                        </td>
                                        <td style={{ padding: '8px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)', fontFamily: 'monospace' }}>
                                            {row.content || '—'}
                                        </td>
                                        <td style={{ padding: '8px', color: 'var(--text)', borderBottom: '1px solid var(--divider)', textAlign: 'right', fontWeight: 600 }}>
                                            {row.click_count}
                                        </td>
                                        <td style={{ padding: '8px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)', whiteSpace: 'nowrap' }} title={row.created_at}>
                                            {row.created_at ? new Date(row.created_at).toLocaleDateString() : ''}
                                        </td>
                                        <td style={{ padding: '8px', borderBottom: '1px solid var(--divider)', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                            <button
                                                className="mc-copy-btn"
                                                style={{ marginRight: 6 }}
                                                onClick={() => handleRowCopy(row)}
                                                title="Copy URL"
                                            >
                                                {rowCopiedId === row.utm_link_id ? <Check size={12} /> : <Copy size={12} />}
                                                {rowCopiedId === row.utm_link_id ? 'Copied' : 'Copy'}
                                            </button>
                                            <button
                                                className="mc-copy-btn"
                                                onClick={() => handleRowDelete(row)}
                                                title="Delete"
                                                style={{ color: 'var(--danger, #dc2626)' }}
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </GlassCard>
        </>
    );
};


// ---------------------------------------------------------------------------
// Tab: Message Match Checker
// ---------------------------------------------------------------------------

const VERDICT_BADGE: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' }> = {
    strong:   { label: 'Strong Match', variant: 'success' },
    moderate: { label: 'Moderate Match', variant: 'warning' },
    weak:     { label: 'Weak Match', variant: 'danger' },
};

const MessageMatchTab: React.FC = () => {
    const [adHeadline, setAdHeadline] = useState('');
    const [adBody, setAdBody] = useState('');
    const [lpHeadline, setLpHeadline] = useState('');
    const [lpBody, setLpBody] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<{ match_score: number; alignment_issues: string[]; recommendations: string[]; verdict: string } | null>(null);

    const handleCheck = async () => {
        if (!adHeadline.trim()) { setError('Ad headline is required.'); return; }
        if (!lpHeadline.trim()) { setError('Landing page headline is required.'); return; }
        setError(null);
        setLoading(true);
        setResult(null);
        try {
            const data = await checkMessageMatch({
                ad_headline: adHeadline.trim(),
                ad_body: adBody.trim(),
                landing_page_headline: lpHeadline.trim(),
                landing_page_body: lpBody.trim(),
            });
            setResult(data);
        } catch (err: any) {
            setError(getApiError(err, 'Message match check failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const verdictInfo = result ? (VERDICT_BADGE[result.verdict] || VERDICT_BADGE['moderate']) : null;

    return (
        <>
            <GlassCard className="mc-form-card">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)', marginBottom: 'var(--space-4)' }}>
                    {/* Ad Copy */}
                    <div>
                        <h3 style={{ margin: '0 0 var(--space-3) 0', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                            Ad Copy
                        </h3>
                        <div className="mc-form-group">
                            <label>Headline *</label>
                            <input
                                type="text"
                                className="mc-text-input"
                                style={{ padding: '10px 14px', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', color: 'var(--text)', fontSize: '0.9rem', fontFamily: 'var(--font-family)', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                                placeholder="e.g. Save 50% on Premium Shoes Today"
                                value={adHeadline}
                                onChange={e => setAdHeadline(e.target.value)}
                            />
                        </div>
                        <div className="mc-form-group">
                            <label>Body (optional)</label>
                            <textarea
                                className="tall"
                                style={{ width: '100%', minHeight: 120, padding: '10px 14px', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', color: 'var(--text)', fontSize: '0.9rem', fontFamily: 'var(--font-family)', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                                placeholder="Ad body text..."
                                value={adBody}
                                onChange={e => setAdBody(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Landing Page */}
                    <div>
                        <h3 style={{ margin: '0 0 var(--space-3) 0', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                            Landing Page
                        </h3>
                        <div className="mc-form-group">
                            <label>Headline *</label>
                            <input
                                type="text"
                                className="mc-text-input"
                                style={{ padding: '10px 14px', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', color: 'var(--text)', fontSize: '0.9rem', fontFamily: 'var(--font-family)', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                                placeholder="e.g. Exclusive 50% Discount on All Premium Footwear"
                                value={lpHeadline}
                                onChange={e => setLpHeadline(e.target.value)}
                            />
                        </div>
                        <div className="mc-form-group">
                            <label>Body (optional)</label>
                            <textarea
                                className="tall"
                                style={{ width: '100%', minHeight: 120, padding: '10px 14px', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', color: 'var(--text)', fontSize: '0.9rem', fontFamily: 'var(--font-family)', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                                placeholder="Landing page body text..."
                                value={lpBody}
                                onChange={e => setLpBody(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {error && <ErrorState message={error} />}
                <div className="mc-form-actions">
                    <GlassButton variant="primary" onClick={handleCheck} disabled={loading}>
                        <MousePointer size={16} />
                        {loading ? 'Checking...' : 'Check Message Match'}
                    </GlassButton>
                </div>
                {loading && <LoadingState message="Analysing message alignment..." />}
            </GlassCard>

            {result && !loading && verdictInfo && (
                <GlassCard className="mc-results-card">
                    {/* Score & verdict */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-4)', padding: 'var(--space-4)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--stroke)' }}>
                        <div style={{ textAlign: 'center', minWidth: 80 }}>
                            <div style={{ fontSize: '2.5rem', fontWeight: 'var(--font-bold)', color: result.match_score >= 70 ? 'var(--success, #22c55e)' : result.match_score >= 40 ? 'var(--warning, #f59e0b)' : 'var(--danger)', lineHeight: 1 }}>
                                {result.match_score}
                            </div>
                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 4 }}>/ 100</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', marginBottom: 4 }}>
                                {verdictInfo.label}
                            </div>
                            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                Message alignment score between your ad and landing page
                            </div>
                        </div>
                    </div>

                    {/* Issues */}
                    {result.alignment_issues && result.alignment_issues.length > 0 && (
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            <h4 style={{ margin: '0 0 var(--space-2) 0', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--danger)' }}>
                                Alignment Issues
                            </h4>
                            <ul style={{ margin: 0, padding: '0 0 0 var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                                {result.alignment_issues.map((issue, i) => (
                                    <li key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{issue}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Recommendations */}
                    {result.recommendations && result.recommendations.length > 0 && (
                        <div>
                            <h4 style={{ margin: '0 0 var(--space-2) 0', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                                Recommendations
                            </h4>
                            <ul style={{ margin: 0, padding: '0 0 0 var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                                {result.recommendations.map((rec, i) => (
                                    <li key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{rec}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </GlassCard>
            )}
        </>
    );
};


// ---------------------------------------------------------------------------
// Save to Project — reusable row for stateless tabs
// ---------------------------------------------------------------------------

interface SaveToProjectRowProps {
    projects: Project[];
    defaultProjectId?: string;
    contentType: string;
    contentData: any;
    sourceTaskId?: string;
    articleTitle?: string;
}

const SaveToProjectRow: React.FC<SaveToProjectRowProps> = ({
    projects, defaultProjectId, contentType, contentData, sourceTaskId, articleTitle,
}) => {
    const [projectId, setProjectId] = useState(defaultProjectId || '');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (defaultProjectId && !projectId) setProjectId(defaultProjectId);
    }, [defaultProjectId, projectId]);

    const handleSave = async () => {
        if (!projectId) { setSaveError('Select a project first.'); return; }
        setSaving(true);
        setSaveError(null);
        try {
            await saveMultichannelOutput({
                project_id: projectId,
                source_task_id: sourceTaskId,
                content_type: contentType,
                content_data: contentData,
                article_title: articleTitle,
            });
            setSaved(true);
        } catch (err: any) {
            setSaveError(getApiError(err, 'Save failed.'));
        } finally {
            setSaving(false);
        }
    };

    if (saved) {
        return (
            <div className="mc-save-row mc-saved">
                <CheckCircle2 size={16} />
                <span>Saved to project</span>
            </div>
        );
    }

    return (
        <div className="mc-save-row">
            <GlassSelect
                options={[
                    { value: '', label: 'Select project...' },
                    ...projects.map(p => ({ value: p.project_id, label: p.name })),
                ]}
                value={projectId}
                onChange={setProjectId}
                placeholder="Select project..."
            />
            <GlassButton variant="secondary" onClick={handleSave} disabled={saving || !projectId}>
                <Save size={14} />
                {saving ? 'Saving...' : 'Save to Project'}
            </GlassButton>
            {saveError && <span className="mc-save-error">{saveError}</span>}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Repurpose Article Tab — project-connected pipeline (receives lifted state)
// ---------------------------------------------------------------------------

interface RepurposeArticleTabProps {
    projects: Project[];
    projectId: string;
    onProjectChange: (id: string) => void;
    articles: ProjectArticle[];
    loadingArticles: boolean;
    selectedArticleId: string;
    onArticleChange: (id: string) => void;
    brandGuides: BrandGuide[];
    brandGuideId: string;
    onBrandGuideChange: (id: string) => void;
    result: RepurposeArticleResponse | null;
    onResult: (r: RepurposeArticleResponse | null) => void;
}

const RepurposeArticleTab: React.FC<RepurposeArticleTabProps> = ({
    projects, projectId, onProjectChange,
    articles, loadingArticles, selectedArticleId, onArticleChange,
    brandGuides, brandGuideId, onBrandGuideChange,
    result, onResult,
}) => {
    const [autoImage, setAutoImage] = useState(true);
    const [emailDrip, setEmailDrip] = useState(true);
    const [generateAdCopy, setGenerateAdCopy] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
    const [streamFilter, setStreamFilter] = useState('');
    const [manualText, setManualText] = useState('');
    const [manualLoading, setManualLoading] = useState(false);

    // Derive unique streams from articles
    const streams = Array.from(new Set(articles.map(a => a.masterplan_stream).filter(Boolean))).sort();
    const filteredArticles = streamFilter
        ? articles.filter(a => a.masterplan_stream === streamFilter)
        : articles;

    const toggleSection = (key: string) =>
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

    const handleRepurpose = async () => {
        if (!projectId) { setError('Select a project.'); return; }
        if (!selectedArticleId) { setError('Select an article.'); return; }
        setError(null);
        setLoading(true);
        onResult(null);
        try {
            const data = await repurposeArticle({
                task_id: selectedArticleId,
                project_id: projectId,
                brand_guide_id: brandGuideId || undefined,
                generate_email_drip: emailDrip,
                auto_fetch_image: autoImage,
                generate_ad_copy: generateAdCopy,
            });
            onResult(data);
            setExpandedSections({ social: true, carousel: false, video: false, quotes: false, email: false, adcopy: generateAdCopy });
        } catch (err: any) {
            setError(getApiError(err, 'Repurposing failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleRepurposeText = async () => {
        if (!projectId) { setError('Select a project first.'); return; }
        if (!manualText.trim()) { setError('Enter some content to repurpose.'); return; }
        setError(null);
        setManualLoading(true);
        onResult(null);
        // Use the repurposeArticle with a special marker — backend will accept content directly
        // For now, save the text as content and use the existing pipeline
        try {
            const data = await repurposeArticle({
                task_id: '__manual__',
                project_id: projectId,
                brand_guide_id: brandGuideId || undefined,
                generate_email_drip: emailDrip,
                auto_fetch_image: false,
            });
            onResult(data);
        } catch {
            // Manual text repurposing not supported by backend yet — show message
            setError('Manual text repurposing requires an article. Please select one from the dropdown.');
        } finally {
            setManualLoading(false);
        }
    };

    const selectedArticle = articles.find(a => a.task_id === selectedArticleId);

    return (
        <>
            <GlassCard className="mc-form-card">
                {/* Row 1: Project */}
                <div className="mc-form-group">
                    <label>Project *</label>
                    <GlassSelect
                        options={[
                            { value: '', label: 'Select project...' },
                            ...projects.map(p => ({ value: p.project_id, label: p.name })),
                        ]}
                        value={projectId}
                        onChange={onProjectChange}
                        placeholder="Select project..."
                    />
                </div>

                {/* Row 2: Stream filter + Article */}
                <div className="mc-form-grid">
                    {streams.length > 1 && (
                        <div className="mc-form-group">
                            <label>Stream</label>
                            <GlassSelect
                                options={[
                                    { value: '', label: 'All streams' },
                                    ...streams.map(s => ({ value: s, label: s })),
                                ]}
                                value={streamFilter}
                                onChange={(val: string) => { setStreamFilter(val); onArticleChange(''); }}
                                placeholder="Filter by stream..."
                            />
                        </div>
                    )}
                    <div className="mc-form-group">
                        <label>Article *</label>
                        {loadingArticles ? (
                            <div className="mc-loading" style={{ padding: '8px 0' }}>
                                <div className="mc-spinner" style={{ width: 16, height: 16 }} />
                                <span style={{ fontSize: '0.85rem' }}>Loading articles...</span>
                            </div>
                        ) : (
                            <GlassSelect
                                options={[
                                    { value: '', label: filteredArticles.length ? 'Select article...' : 'No completed articles' },
                                    ...filteredArticles.map(a => ({
                                        value: a.task_id,
                                        label: `${a.name}${a.has_hero_image ? '' : ' (no image)'}`,
                                    })),
                                ]}
                                value={selectedArticleId}
                                onChange={onArticleChange}
                                placeholder="Select article..."
                            />
                        )}
                    </div>
                </div>

                {/* Manual text input — visible when no article is selected */}
                {!selectedArticleId && (
                    <div className="mc-form-group">
                        <label>Or paste content manually...</label>
                        <textarea
                            className="tall"
                            placeholder="Paste your article, blog post, or any content here to repurpose it across channels..."
                            value={manualText}
                            onChange={e => setManualText(e.target.value)}
                        />
                        {manualText.trim() && (
                            <div className="mc-form-actions" style={{ marginTop: 8 }}>
                                <GlassButton variant="secondary" onClick={handleRepurposeText} disabled={manualLoading || !projectId}>
                                    <Repeat2 size={14} />
                                    {manualLoading ? 'Repurposing...' : 'Repurpose Text'}
                                </GlassButton>
                            </div>
                        )}
                    </div>
                )}

                <BrandGuideSelector brandGuideId={brandGuideId} brandGuides={brandGuides} onChange={onBrandGuideChange} />

                <div className="mc-option-checkboxes">
                    <label className={`mc-platform-checkbox ${autoImage ? 'checked' : ''}`}>
                        <input type="checkbox" checked={autoImage} onChange={e => setAutoImage(e.target.checked)} />
                        <Image size={14} /> Auto-fetch hero image
                    </label>
                    <label className={`mc-platform-checkbox ${emailDrip ? 'checked' : ''}`}>
                        <input type="checkbox" checked={emailDrip} onChange={e => setEmailDrip(e.target.checked)} />
                        <Mail size={14} /> Generate email drip sequence
                    </label>
                    <label className={`mc-platform-checkbox ${generateAdCopy ? 'checked' : ''}`}>
                        <input type="checkbox" checked={generateAdCopy} onChange={e => setGenerateAdCopy(e.target.checked)} />
                        <Megaphone size={14} /> Generate ad copy (Google, Meta, LinkedIn)
                    </label>
                </div>

                {error && <ErrorState message={error} />}

                <div className="mc-form-actions">
                    <GlassButton variant="primary" onClick={handleRepurpose} disabled={loading || !selectedArticleId}>
                        <Repeat2 size={16} />
                        {loading ? 'Repurposing...' : 'Repurpose Article'}
                    </GlassButton>
                </div>
                {loading && (
                    <LoadingState message="Generating content for all channels. This may take 2-5 minutes..." />
                )}
            </GlassCard>

            {result && !loading && (
                <GlassCard className="mc-results-card mc-repurpose-article-results">
                    {/* Summary badges */}
                    <div className="mc-repurpose-summary">
                        <h3>Repurpose Complete: {selectedArticle?.name || 'Article'}</h3>
                        <div className="mc-summary-badges">
                            {result.summary?.social_posts && <span className="mc-badge mc-badge-success">Social Posts</span>}
                            {result.summary?.carousel && <span className="mc-badge mc-badge-success">Carousel</span>}
                            {result.summary?.video_script && <span className="mc-badge mc-badge-success">Video Script</span>}
                            {result.summary?.pull_quotes && <span className="mc-badge mc-badge-success">Pull Quotes</span>}
                            {result.summary?.email_drip && <span className="mc-badge mc-badge-success">Email Drip</span>}
                            {result.summary?.ad_copy && <span className="mc-badge mc-badge-success">Ad Copy</span>}
                            {result.summary?.hero_image && <span className="mc-badge mc-badge-success">Hero Image</span>}
                        </div>
                    </div>

                    {/* Hero image preview */}
                    {result.hero_image && (
                        <div className="mc-hero-preview">
                            <img
                                src={result.hero_image.urls?.large2x || result.hero_image.urls?.large || result.hero_image.large || result.hero_image.url || result.hero_image.src || ''}
                                alt={result.hero_image.description || result.hero_image.alt || 'Hero image'}
                            />
                            <span className="mc-hero-credit">
                                Photo by {result.hero_image.photographer || 'Unknown'} on Pexels
                            </span>
                        </div>
                    )}

                    {/* Collapsible sections */}
                    {result.repurpose_result?.social_posts && (
                        <div className="mc-collapsible-section">
                            <button className="mc-collapsible-header" onClick={() => toggleSection('social')}>
                                <div className="mc-section-header">
                                    <div className="mc-section-icon"><Share2 size={16} /></div>
                                    <h3>Social Media Posts</h3>
                                </div>
                                {expandedSections.social ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            {expandedSections.social && (
                                <div className="mc-collapsible-body">
                                    <div className="mc-post-grid">
                                        {Object.entries(result.repurpose_result.social_posts).filter(([platform]) => RENDERABLE_PLATFORMS.has(platform)).map(([platform, text]) => (
                                            <div className="mc-post-item" key={platform}>
                                                <div className="mc-post-label">
                                                    {platform.replace(/_/g, ' ').toUpperCase()}
                                                    <CopyButton text={String(text)} />
                                                </div>
                                                <div className="mc-post-text">{String(text)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {result.repurpose_result?.carousel_outline && (
                        <div className="mc-collapsible-section">
                            <button className="mc-collapsible-header" onClick={() => toggleSection('carousel')}>
                                <div className="mc-section-header">
                                    <div className="mc-section-icon"><Layers size={16} /></div>
                                    <h3>LinkedIn Carousel ({result.repurpose_result.carousel_outline.slides?.length || 0} slides)</h3>
                                </div>
                                {expandedSections.carousel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            {expandedSections.carousel && (
                                <div className="mc-collapsible-body">
                                    <div className="mc-slide-list">
                                        {(result.repurpose_result.carousel_outline.slides || []).map(slide => (
                                            <div className="mc-slide-card" key={slide.slide_number}>
                                                <div className="mc-slide-number">{slide.slide_number}</div>
                                                <div className="mc-slide-content">
                                                    <div className="mc-slide-headline">{slide.headline}</div>
                                                    <div className="mc-slide-body">{slide.body}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {result.repurpose_result?.video_script && (
                        <div className="mc-collapsible-section">
                            <button className="mc-collapsible-header" onClick={() => toggleSection('video')}>
                                <div className="mc-section-header">
                                    <div className="mc-section-icon"><Video size={16} /></div>
                                    <h3>Video Script</h3>
                                </div>
                                {expandedSections.video ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            {expandedSections.video && (
                                <div className="mc-collapsible-body mc-video-script">
                                    <div className="mc-script-field">
                                        <div className="mc-script-label">Hook</div>
                                        <div className="mc-script-content">{result.repurpose_result.video_script.hook}</div>
                                    </div>
                                    <div className="mc-script-field">
                                        <div className="mc-script-label">Talking Points</div>
                                        <ul className="mc-talking-points">
                                            {(result.repurpose_result.video_script.talking_points || []).map((tp, i) => (
                                                <li key={i} data-num={i + 1}>{tp}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="mc-script-field">
                                        <div className="mc-script-label">CTA</div>
                                        <div className="mc-script-content">{result.repurpose_result.video_script.cta}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {result.repurpose_result?.pull_quotes && result.repurpose_result.pull_quotes.length > 0 && (
                        <div className="mc-collapsible-section">
                            <button className="mc-collapsible-header" onClick={() => toggleSection('quotes')}>
                                <div className="mc-section-header">
                                    <div className="mc-section-icon"><Quote size={16} /></div>
                                    <h3>Pull Quotes ({result.repurpose_result.pull_quotes.length})</h3>
                                </div>
                                {expandedSections.quotes ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            {expandedSections.quotes && (
                                <div className="mc-collapsible-body mc-pull-quotes">
                                    {result.repurpose_result.pull_quotes.map((q, i) => (
                                        <div className="mc-pull-quote" key={i}>
                                            <span className="mc-pull-quote-mark">&ldquo;</span>
                                            <div className="mc-pull-quote-text">{q}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Ad Copy section */}
                    {result.repurpose_result?.ad_copy && !result.repurpose_result.ad_copy._error && (
                        <div className="mc-collapsible-section">
                            <button className="mc-collapsible-header" onClick={() => toggleSection('adcopy')}>
                                <div className="mc-section-header">
                                    <div className="mc-section-icon"><Megaphone size={16} /></div>
                                    <h3>Ad Copy (Google · Meta · LinkedIn)</h3>
                                </div>
                                {expandedSections.adcopy ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            {expandedSections.adcopy && (
                                <div className="mc-collapsible-body">
                                    {/* Google */}
                                    {result.repurpose_result.ad_copy.google && (
                                        <div className="mc-ad-platform-card" style={{ marginBottom: 12 }}>
                                            <div className="mc-ad-platform-header">
                                                <span className="mc-ad-platform-badge google">Google Ads</span>
                                            </div>
                                            <div className="mc-ad-field">
                                                <span className="mc-ad-field-label">Headlines</span>
                                                <div className="mc-ad-headlines">
                                                    {(result.repurpose_result.ad_copy.google.headlines || []).map((h, i) => (
                                                        <div key={i} className="mc-ad-headline-item">
                                                            <span style={{ flex: 1 }}>{h}</span>
                                                            <span className={`mc-ad-headline-len ${h.length > 30 ? 'over' : 'ok'}`}>{h.length}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            {(result.repurpose_result.ad_copy.google.descriptions || []).map((d, i) => (
                                                <div key={i} className="mc-ad-field">
                                                    <span className="mc-ad-field-label">Description {i + 1} <span className={d.length > 90 ? 'mc-ad-over' : ''}>{d.length}/90</span></span>
                                                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text)' }}>{d}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* Meta */}
                                    {result.repurpose_result.ad_copy.meta && (
                                        <div className="mc-ad-platform-card" style={{ marginBottom: 12 }}>
                                            <div className="mc-ad-platform-header">
                                                <span className="mc-ad-platform-badge meta">Meta</span>
                                            </div>
                                            {result.repurpose_result.ad_copy.meta.primary_text && (
                                                <div className="mc-ad-field">
                                                    <span className="mc-ad-field-label">Primary Text</span>
                                                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text)' }}>{result.repurpose_result.ad_copy.meta.primary_text}</p>
                                                </div>
                                            )}
                                            {result.repurpose_result.ad_copy.meta.headline && (
                                                <div className="mc-ad-field">
                                                    <span className="mc-ad-field-label">Headline</span>
                                                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text)' }}>{result.repurpose_result.ad_copy.meta.headline}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {/* LinkedIn */}
                                    {result.repurpose_result.ad_copy.linkedin && (
                                        <div className="mc-ad-platform-card">
                                            <div className="mc-ad-platform-header">
                                                <span className="mc-ad-platform-badge linkedin">LinkedIn</span>
                                            </div>
                                            {result.repurpose_result.ad_copy.linkedin.intro_text && (
                                                <div className="mc-ad-field">
                                                    <span className="mc-ad-field-label">Intro Text</span>
                                                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text)' }}>{result.repurpose_result.ad_copy.linkedin.intro_text}</p>
                                                </div>
                                            )}
                                            {result.repurpose_result.ad_copy.linkedin.headline && (
                                                <div className="mc-ad-field">
                                                    <span className="mc-ad-field-label">Headline</span>
                                                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text)' }}>{result.repurpose_result.ad_copy.linkedin.headline}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {result.email_drip && result.email_drip.emails && (
                        <div className="mc-collapsible-section">
                            <button className="mc-collapsible-header" onClick={() => toggleSection('email')}>
                                <div className="mc-section-header">
                                    <div className="mc-section-icon"><Mail size={16} /></div>
                                    <h3>Email Drip Sequence ({result.email_drip.emails.length} emails)</h3>
                                </div>
                                {expandedSections.email ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            {expandedSections.email && (
                                <div className="mc-collapsible-body mc-drip-cards">
                                    {result.email_drip.emails.map((email, i) => (
                                        <div className="mc-drip-card" key={i}>
                                            <div className="mc-drip-card-header">
                                                <span className="mc-drip-step">Step {email.step_order}</span>
                                                <span className="mc-drip-delay">Day {email.delay_days}</span>
                                            </div>
                                            <div className="mc-drip-subject">
                                                <strong>Subject:</strong> {email.subject_line}
                                            </div>
                                            <div className="mc-drip-preview">
                                                <strong>Preview:</strong> {email.preview_text}
                                            </div>
                                            <div className="mc-drip-body" dangerouslySetInnerHTML={{ __html: email.body_html }} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Main Module — owns lifted state for cross-tab persistence
// ---------------------------------------------------------------------------

const MultiChannelModule: React.FC = () => {
    const ctx = useOutletContext<{ selectedProjectId?: string }>();
    const defaultProjectId = ctx?.selectedProjectId;

    const [activeTab, setActiveTab] = useState('repurpose-article');
    const [brandGuides, setBrandGuides] = useState<BrandGuide[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);

    // Lifted state — persists across tab switches
    const [projectId, setProjectId] = useState(defaultProjectId || '');
    const [articles, setArticles] = useState<ProjectArticle[]>([]);
    const [loadingArticles, setLoadingArticles] = useState(false);
    const [selectedArticleId, setSelectedArticleId] = useState('');
    const [brandGuideId, setBrandGuideId] = useState('');
    const [repurposeResult, setRepurposeResult] = useState<RepurposeArticleResponse | null>(null);
    const [savedTasks, setSavedTasks] = useState<Record<string, SavedTaskRef>>({});

    // Init: load all brand guides + projects
    useEffect(() => {
        getBrandGuides().then(setBrandGuides).catch(() => {});
        getProjects(false).then(setProjects).catch(() => {});
    }, []);

    // Sync from sidebar context
    useEffect(() => {
        if (defaultProjectId && !projectId) setProjectId(defaultProjectId);
    }, [defaultProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-detect brand guide when project changes
    useEffect(() => {
        if (!projectId) { setBrandGuideId(''); return; }
        getBrandGuidesByProject(projectId)
            .then(guides => {
                // Pick the default brand guide, or the first one
                const def = guides.find(g => (g as any).is_default === 1 || (g as any).is_default === true);
                setBrandGuideId(def ? def.brand_guide_id : (guides[0]?.brand_guide_id || ''));
            })
            .catch(() => setBrandGuideId(''));
    }, [projectId]);

    // Load articles when project changes
    useEffect(() => {
        if (!projectId) { setArticles([]); setSelectedArticleId(''); setRepurposeResult(null); return; }
        setLoadingArticles(true);
        setRepurposeResult(null);
        getProjectArticles(projectId)
            .then(data => { setArticles(data); setSelectedArticleId(''); })
            .catch(() => setArticles([]))
            .finally(() => setLoadingArticles(false));
    }, [projectId]);

    // Load stored multichannel content when article changes
    const handleArticleChange = useCallback((id: string) => {
        setSelectedArticleId(id);
        setRepurposeResult(null);
        setSavedTasks({});
        if (id) {
            getArticleMultichannelContent(id)
                .then(resp => {
                    if (resp.multichannel) {
                        const mc = resp.multichannel;
                        // Ensure summary exists (may be missing from older stored data)
                        if (!mc.summary) {
                            const rr = mc.repurpose_result;
                            mc.summary = {
                                social_posts: !!rr?.social_posts,
                                carousel: !!rr?.carousel_outline,
                                video_script: !!rr?.video_script,
                                pull_quotes: !!(rr?.pull_quotes && rr.pull_quotes.length),
                                email_drip: !!(mc.email_drip && mc.email_drip.emails),
                                hero_image: !!mc.hero_image,
                            };
                        }
                        setRepurposeResult(mc);
                    }
                    if (resp.saved_tasks) {
                        setSavedTasks(resp.saved_tasks);
                    }
                })
                .catch(() => {
                    // No stored content — that's fine
                });
        }
    }, []);

    // Derive article name for tabs
    const selectedArticleName = articles.find(a => a.task_id === selectedArticleId)?.name || '';

    if (!defaultProjectId) return (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
            <Share2 size={64} style={{ opacity: 0.4, marginBottom: '16px' }} />
            <h3 style={{ color: 'var(--text)', marginBottom: '8px' }}>Select a project</h3>
            <p>Choose a project from the sidebar to view multi-channel content</p>
        </div>
    );

    return (
        <div className="mc-page">
            <ModuleHeader
                title="Multi-Channel Content"
                breadcrumb={['Multi-Channel']}
            />

            {/* Active article indicator */}
            {selectedArticleName && repurposeResult && (
                <div className="mc-active-article-bar">
                    <FileText size={14} />
                    <span>Working with: <strong>{selectedArticleName}</strong></span>
                </div>
            )}

            <div className="mc-tabs-row">
                <GlassTabs
                    tabs={TABS}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                />
            </div>

            {activeTab === 'repurpose-article' && (
                <RepurposeArticleTab
                    projects={projects}
                    projectId={projectId}
                    onProjectChange={setProjectId}
                    articles={articles}
                    loadingArticles={loadingArticles}
                    selectedArticleId={selectedArticleId}
                    onArticleChange={handleArticleChange}
                    brandGuides={brandGuides}
                    brandGuideId={brandGuideId}
                    onBrandGuideChange={setBrandGuideId}
                    result={repurposeResult}
                    onResult={setRepurposeResult}
                />
            )}
            {activeTab === 'social-posts' && (
                <SocialPostsTab
                    repurposeResult={repurposeResult}
                    onUpdate={setRepurposeResult}
                    projectId={projectId}
                    selectedArticleId={selectedArticleId}
                    brandGuideId={brandGuideId}
                    onSwitchTab={setActiveTab}
                    savedTaskRef={savedTasks['social_posts']}
                    onDeleteSaved={() => setSavedTasks(prev => { const { social_posts: _, ...rest } = prev; return rest; })}
                />
            )}
            {activeTab === 'thread' && (
                <ThreadBuilderTab
                    brandGuides={brandGuides}
                    projects={projects}
                    defaultProjectId={projectId}
                    prefill={repurposeResult?.repurpose_result?.social_posts?.twitter_thread ? String(repurposeResult.repurpose_result.social_posts.twitter_thread) : undefined}
                    brandGuideId={brandGuideId}
                    repurposeResult={repurposeResult}
                    onUpdate={setRepurposeResult}
                    selectedArticleId={selectedArticleId}
                    projectId={projectId}
                    onSwitchTab={setActiveTab}
                />
            )}
            {activeTab === 'carousel' && (
                <LinkedInCarouselTab
                    brandGuides={brandGuides}
                    projects={projects}
                    defaultProjectId={projectId}
                    prefillResult={repurposeResult?.repurpose_result?.carousel_outline || undefined}
                    prefillTopic={selectedArticleName}
                    brandGuideId={brandGuideId}
                    repurposeResult={repurposeResult}
                    onUpdate={setRepurposeResult}
                    selectedArticleId={selectedArticleId}
                    projectId={projectId}
                    onSwitchTab={setActiveTab}
                />
            )}
            {activeTab === 'video-script' && (
                <VideoScriptTab
                    repurposeResult={repurposeResult}
                    onUpdate={setRepurposeResult}
                    projectId={projectId}
                    selectedArticleId={selectedArticleId}
                    brandGuideId={brandGuideId}
                    onSwitchTab={setActiveTab}
                />
            )}
            {activeTab === 'email-sequence' && (
                <EmailSequenceTab
                    repurposeResult={repurposeResult}
                    onUpdate={setRepurposeResult}
                    projectId={projectId}
                    selectedArticleId={selectedArticleId}
                    brandGuideId={brandGuideId}
                    onSwitchTab={setActiveTab}
                />
            )}
            {activeTab === 'adcopy' && <AdCopyTab brandGuides={brandGuides} projects={projects} defaultProjectId={projectId} brandGuideId={brandGuideId} />}
            {activeTab === 'utm' && <UtmBuilderTab projectId={projectId} />}
            {activeTab === 'message-match' && <MessageMatchTab />}
        </div>
    );
};

export default MultiChannelModule;
